// src/features/data/blacklist-tab.tsx
import { DataTable, Kicker, StatusPill } from "@/components/iris";
import type { DataTableColumn } from "@/components/iris";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import {
  useAddBlacklistEntry,
  useBlacklist,
  useDeleteBlacklistEntry,
  useFleetStats,
  useSetAllAvatarCoverage,
  useSetAvatarCoverage,
} from "@/hooks/use-fleet";
import type { BlacklistEntry } from "@/lib/types";
import { Download, Eye, EyeOff, Plus, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import {
  PLATFORM_OPTIONS,
  TYPE_OPTIONS,
  canSubmitEntryForm,
  isIdentifierMasked,
  matchesBlacklistSearch,
  statusPillVariant,
  typePillVariant,
} from "./blacklist-tab.helpers";
import { useClientSort } from "./use-client-sort";

// ─── Helpers ───

function fmtDate(iso: string | null): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// IR-1045 P5.1 (Rotem 06-16): phone identifiers show masked (****NNNN); an eye
// toggle reveals the full raw value (already on the row) so an analyst can verify
// the number \u2014 same affordance as a password reveal. Non-maskable rows (name /
// platform_id, where masked == raw) render plain with no toggle.
function IdentifierCell({ entry }: { entry: BlacklistEntry }) {
  const [revealed, setRevealed] = useState(false);
  const maskable = isIdentifierMasked(entry);
  // Show the full value whenever it isn't maskable (avatar-managed rows, or
  // already-plain name/platform_id types) — only genuinely masked targets stay
  // hidden until revealed.
  const shown = revealed || !maskable ? entry.identifier : entry.masked_identifier;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: "var(--iris-fg-1)" }}>{shown}</span>
      {maskable && (
        <button
          type="button"
          aria-label={revealed ? "Hide full identifier" : "Reveal full identifier"}
          title={revealed ? "Hide" : "Reveal full number"}
          className="iris-btn"
          style={{
            padding: 2,
            background: "transparent",
            border: "none",
            color: "var(--iris-fg-4)",
            cursor: "pointer",
          }}
          onClick={() => setRevealed((v) => !v)}
        >
          {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
      )}
    </span>
  );
}

// ─── KPI Row ───

function KpiRow() {
  const { data: stats } = useFleetStats();
  const items = [
    { label: "Total Blacklisted", value: stats?.total_blacklist, warn: false },
    { label: "Phone Numbers", value: stats?.blacklist_phones, warn: false },
    { label: "Platform IDs", value: stats?.blacklist_platform_ids, warn: false },
    {
      label: "Expiring Soon",
      value: stats?.blacklist_expiring_soon,
      warn: (stats?.blacklist_expiring_soon ?? 0) > 0,
    },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      {items.map(({ label, value, warn }) => (
        <div
          key={label}
          className="iris-card"
          style={{
            padding: 16,
            borderColor: warn ? "rgba(255,153,51,0.3)" : undefined,
            background: warn ? "rgba(255,153,51,0.06)" : undefined,
          }}
        >
          <Kicker variant="muted">{label}</Kicker>
          <div
            className="iris-kpi"
            style={{
              marginTop: 4,
              fontSize: 24,
              color: warn ? "var(--iris-neon-orange)" : "var(--iris-fg-1)",
            }}
          >
            {value != null ? value.toLocaleString() : "\u2014"}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Add Entry Dialog ───

type EntryFormState = {
  identifier: string;
  identifier_type: string;
  platform: string;
  platform_native_id: string;
};

const EMPTY_FORM: EntryFormState = {
  identifier: "",
  identifier_type: "phone",
  platform: "",
  platform_native_id: "",
};

function AddEntryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState<EntryFormState>({ ...EMPTY_FORM });
  const addEntry = useAddBlacklistEntry();

  const patch = (p: Partial<EntryFormState>) => setForm((prev) => ({ ...prev, ...p }));

  const canSubmit = canSubmitEntryForm(form);

  function handleSubmit() {
    const payload: Partial<BlacklistEntry> = {
      identifier: form.identifier,
      identifier_type: form.identifier_type,
      platform: form.platform || null,
      platform_native_id: form.platform_native_id || null,
    };
    addEntry.mutate(payload, { onSuccess: onClose });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-[420px]">
        <DialogTitle>Add Blacklist Entry</DialogTitle>
        <DialogDescription>Block an identifier from fleet operations</DialogDescription>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="iris-meta">Identifier</span>
            <input
              value={form.identifier}
              onChange={(e) => patch({ identifier: e.target.value })}
              placeholder="+1234567890 or platform_user_id"
              className="iris-input"
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-meta">Type</span>
              <select
                value={form.identifier_type}
                onChange={(e) => patch({ identifier_type: e.target.value })}
                className="iris-input"
                style={{ cursor: "pointer" }}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-meta">Platform</span>
              <select
                value={form.platform}
                onChange={(e) => patch({ platform: e.target.value })}
                className="iris-input"
                style={{ cursor: "pointer" }}
              >
                <option value="">Select…</option>
                {PLATFORM_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="iris-meta">Platform ID (optional)</span>
            <input
              value={form.platform_native_id}
              onChange={(e) => patch({ platform_native_id: e.target.value })}
              placeholder="447774735108@c.us"
              className="iris-input"
            />
          </label>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
            <button type="button" className="iris-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="iris-btn iris-btn--primary"
              disabled={!canSubmit || addEntry.isPending}
              onClick={handleSubmit}
            >
              Add to Blacklist
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bulk Import Dialog ───

function BulkImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [raw, setRaw] = useState("");
  const [type, setType] = useState("phone");
  const [platform, setPlatform] = useState("");
  const addEntry = useAddBlacklistEntry();
  const [progress, setProgress] = useState<string>("");

  async function handleImport() {
    const lines = raw
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    setProgress(`Importing 0 / ${lines.length}...`);
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] as string;
      const bulkPayload: Partial<BlacklistEntry> = {
        identifier: line,
        identifier_type: type,
        reason: "Bulk import",
      };
      if (platform) bulkPayload.platform = platform;
      await addEntry.mutateAsync(bulkPayload);
      setProgress(`Importing ${i + 1} / ${lines.length}...`);
    }
    setProgress("");
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-[440px]">
        <DialogTitle>Bulk Import</DialogTitle>
        <DialogDescription>Paste identifiers (one per line)</DialogDescription>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-meta">Type</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="iris-input"
                style={{ cursor: "pointer" }}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-meta">Platform</span>
              <input
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                placeholder="telegram"
                className="iris-input"
              />
            </label>
          </div>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="iris-meta">Identifiers (one per line)</span>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={8}
              placeholder={"+1234567890\n+0987654321"}
              className="iris-input"
              style={{ resize: "none", fontFamily: "var(--iris-font-mono)" }}
            />
          </label>

          {progress && <div style={{ fontSize: 11, color: "var(--iris-fg-3)" }}>{progress}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
            <button type="button" className="iris-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="iris-btn iris-btn--primary"
              disabled={!raw.trim() || addEntry.isPending}
              onClick={handleImport}
            >
              Import
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── CSV Export ───

function exportCsv(entries: BlacklistEntry[]) {
  const header =
    "id,identifier,masked_identifier,avatar_name,identifier_type,platform,platform_native_id,status,added_by,added_at";
  const rows = entries.map((e) =>
    [
      e.id,
      e.identifier,
      e.masked_identifier,
      e.avatar_name ?? "",
      e.identifier_type,
      e.platform ?? "",
      e.platform_native_id ?? "",
      e.status ?? "",
      e.added_by ?? "",
      e.added_at,
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `blacklist-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main ───

export function BlacklistTab() {
  // IR-1045 Phase 1: type/platform filters are client-side multi-selects (the
  // endpoint returns the full list, not server-paginated). IR-1257 reopen: search
  // also moves client-side so it can match the ENRICHED avatar_name / phone the
  // operator sees (the backend `?search=` only filtered the raw `identifier`
  // column, so a name search returned nothing — Rotem 07-24).
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data: entries, isLoading } = useBlacklist();
  const deleteEntry = useDeleteBlacklistEntry();
  const setCoverage = useSetAvatarCoverage();
  const setAllCoverage = useSetAllAvatarCoverage();
  // IR-1225: multi-select keyed by avatar_id (manual/non-avatar rows aren't
  // selectable). Mirrors the entities-env-tab Set<> pattern.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toggleSelectId = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const list = entries ?? [];

  const typeOptions = TYPE_OPTIONS.map((t) => ({ value: t, label: t }));
  const platformOptions = [...new Set(list.map((e) => e.platform).filter((p): p is string => !!p))]
    .sort()
    .map((p) => ({ value: p, label: p }));

  const filtered = list.filter(
    (e) =>
      (typeFilter.length === 0 || typeFilter.includes(e.identifier_type)) &&
      (platformFilter.length === 0 ||
        (e.platform != null && platformFilter.includes(e.platform))) &&
      matchesBlacklistSearch(e, search),
  );

  const toMs = (iso: string | null) => (iso ? new Date(iso).getTime() : null);
  const { sorted, sortKey, sortDirection, onSort } = useClientSort<BlacklistEntry>(filtered, {
    masked_identifier: (e) => e.masked_identifier,
    avatar_name: (e) => e.avatar_name ?? null,
    identifier_type: (e) => e.identifier_type,
    platform: (e) => e.platform,
    platform_native_id: (e) => e.platform_native_id,
    status: (e) => e.status ?? null,
    added_at: (e) => toMs(e.added_at),
  });

  function handleDelete(entry: BlacklistEntry) {
    if (confirm(`Remove "${entry.masked_identifier}" from blacklist?`)) {
      deleteEntry.mutate(entry.id);
    }
  }

  // IR-1225: only avatar-matched rows on the current (sorted/filtered) page are
  // selectable for bulk coverage.
  const selectableIds = sorted.map((e) => e.avatar_id).filter((id): id is number => id != null);
  const allOnPageSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (selectableIds.every((id) => next.has(id))) {
        for (const id of selectableIds) next.delete(id);
      } else {
        for (const id of selectableIds) next.add(id);
      }
      return next;
    });
  };

  const columns: DataTableColumn<BlacklistEntry>[] = [
    {
      // IR-1225: bulk-select for coverage. Only avatar-matched rows are
      // selectable; manual entries render a disabled checkbox.
      key: "__select",
      label: (
        <input
          type="checkbox"
          aria-label="Select all avatars on page"
          checked={allOnPageSelected}
          disabled={selectableIds.length === 0}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectAllOnPage();
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ) as unknown as string,
      render: (entry) => (
        <input
          type="checkbox"
          aria-label={`Select ${entry.avatar_name ?? entry.identifier}`}
          checked={entry.avatar_id != null && selected.has(entry.avatar_id)}
          disabled={entry.avatar_id == null}
          onChange={(e) => {
            e.stopPropagation();
            if (entry.avatar_id != null) toggleSelectId(entry.avatar_id);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ),
    },
    {
      key: "masked_identifier",
      label: "Identifier",
      sortable: true,
      cellVariant: "mono",
      render: (entry) => <IdentifierCell entry={entry} />,
    },
    {
      key: "avatar_name",
      label: "Avatar Name",
      sortable: true,
      render: (entry) => (
        <span style={{ color: "var(--iris-fg-2)" }}>{entry.avatar_name ?? "—"}</span>
      ),
    },
    {
      key: "identifier_type",
      label: "Type",
      sortable: true,
      render: (entry) => (
        <StatusPill variant={typePillVariant(entry.identifier_type)}>
          {entry.identifier_type}
        </StatusPill>
      ),
    },
    {
      key: "platform",
      label: "Platform",
      sortable: true,
      cellVariant: "muted",
      render: (entry) => <span>{entry.platform ?? "\u2014"}</span>,
    },
    {
      key: "platform_native_id",
      label: "Platform ID",
      sortable: true,
      cellVariant: "mono",
      render: (entry) => (
        <span style={{ color: "var(--iris-fg-3)" }}>{entry.platform_native_id ?? "N/A"}</span>
      ),
    },
    {
      // IR-1225: matched avatar's phone (avatar rows only).
      key: "phone_number",
      label: "Phone",
      sortable: true,
      cellVariant: "mono",
      render: (entry) => (
        <span style={{ color: "var(--iris-fg-3)" }}>{entry.phone_number ?? "—"}</span>
      ),
    },
    {
      // IR-1225: coverage state = matched avatar's dev_ingest_exempt. Non-avatar
      // (manual) rows show a dash. "Coverage Disabled · DEV" (orange) means the
      // avatar's own account/messages ingest in DEV only; PROD always excludes.
      // Label uses the AC term "Coverage Disabled" (Rotem: an explicit Disabled marker).
      key: "coverage",
      label: "Coverage",
      render: (entry) =>
        entry.avatar_id == null ? (
          <span style={{ color: "var(--iris-fg-4)" }}>{"—"}</span>
        ) : entry.coverage_disabled ? (
          <span
            title="Blacklist coverage disabled — this avatar ingests its own messages in DEV only. PROD still excludes it."
            style={{
              padding: "1px 6px",
              borderRadius: 3,
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              border: "1px solid var(--iris-neon-orange)",
              color: "var(--iris-neon-orange)",
              whiteSpace: "nowrap",
            }}
          >
            Coverage Disabled · DEV
          </span>
        ) : (
          <span
            style={{
              padding: "1px 6px",
              borderRadius: 3,
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              border: "1px solid var(--iris-fg-4)",
              color: "var(--iris-fg-3)",
            }}
          >
            Covered
          </span>
        ),
    },
    {
      key: "added_at",
      label: "Added",
      sortable: true,
      cellVariant: "muted",
      render: (entry) => <span>{fmtDate(entry.added_at)}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (entry) => (
        <StatusPill variant={statusPillVariant(entry.status)}>
          {entry.status ?? "\u2014"}
        </StatusPill>
      ),
    },
    {
      key: "_actions",
      label: "",
      render: (entry) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
          {/* IR-1225: per-row coverage toggle (avatar rows only). DEV-only. */}
          {entry.avatar_id != null && (
            <button
              type="button"
              className="iris-btn"
              style={{ fontSize: 11, padding: "4px 8px", whiteSpace: "nowrap" }}
              disabled={setCoverage.isPending}
              onClick={() => {
                const disabling = !entry.coverage_disabled;
                const who = entry.avatar_name ?? entry.identifier;
                const msg = disabling
                  ? `Disable blacklist coverage for ${who} (DEV only)? Its own account + messages will be ingested in DEV for testing. PROD always keeps it excluded.`
                  : `Re-enable blacklist coverage for ${who}? It returns to normal exclusion; DEV test data is purged on the next run.`;
                if (!window.confirm(msg)) return;
                setCoverage.mutate({
                  ids: [entry.avatar_id as number],
                  coverageDisabled: disabling,
                });
              }}
            >
              {entry.coverage_disabled ? "Enable coverage" : "Disable coverage"}
            </button>
          )}
          <button
            type="button"
            className="iris-btn"
            style={{
              padding: 4,
              color: "var(--iris-fg-4)",
              background: "transparent",
              border: "none",
            }}
            disabled={deleteEntry.isPending}
            onClick={() => handleDelete(entry)}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <KpiRow />

      {/* Toolbar */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search entries..."
          className="iris-input"
        />
        <MultiSelectFilter
          label="Type"
          options={typeOptions}
          selected={typeFilter}
          onChange={setTypeFilter}
        />
        <MultiSelectFilter
          label="Platform"
          options={platformOptions}
          selected={platformFilter}
          onChange={setPlatformFilter}
        />
        <span className="iris-meta" style={{ marginLeft: "auto" }}>
          {isLoading
            ? "Loading..."
            : `${filtered.length}${filtered.length !== list.length ? ` of ${list.length}` : ""} entries`}
        </span>
        {/* IR-1225: coverage on ALL avatars (DEV-only). Server excludes deleted. */}
        <button
          type="button"
          className="iris-btn"
          disabled={setAllCoverage.isPending}
          onClick={() => {
            if (
              !window.confirm(
                "Disable blacklist coverage (DEV only) for ALL avatars? Every avatar's own account + messages will be ingested in DEV for testing. PROD always keeps them excluded.",
              )
            )
              return;
            setAllCoverage.mutate({ coverageDisabled: true });
          }}
        >
          Disable coverage — all · DEV
        </button>
        <button
          type="button"
          className="iris-btn"
          disabled={setAllCoverage.isPending}
          onClick={() => {
            if (!window.confirm("Re-enable blacklist coverage for ALL avatars?")) return;
            setAllCoverage.mutate({ coverageDisabled: false });
          }}
        >
          Enable — all
        </button>
        <button
          type="button"
          className="iris-btn"
          onClick={() => exportCsv(sorted)}
          disabled={sorted.length === 0}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Download size={13} /> Export CSV
        </button>
        <button
          type="button"
          className="iris-btn"
          onClick={() => setBulkOpen(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Upload size={13} /> Bulk Import
        </button>
        <button
          type="button"
          className="iris-btn iris-btn--primary"
          onClick={() => setAddOpen(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={13} /> Add Entry
        </button>
      </div>

      {/* IR-1225: DEV-only scope note for coverage controls. */}
      <div className="iris-meta" style={{ color: "var(--iris-fg-4)" }}>
        Coverage controls affect DEV ingestion only — PROD always enforces the blacklist.
      </div>

      {/* Table */}
      <div className="iris-card" style={{ padding: 0, overflow: "hidden" }}>
        {isLoading ? (
          <div style={{ padding: "16px 18px", color: "var(--iris-fg-3)", fontSize: 12 }}>
            Loading...
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={sorted}
            rowKey={(entry) => entry.id}
            onSort={onSort}
            sortKey={sortKey}
            sortDirection={sortDirection}
            emptyMessage="No blacklist entries found"
          />
        )}
      </div>

      {/* IR-1225: sticky bulk-coverage bar for selected avatars (DEV-only). */}
      {selected.size > 0 && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "8px 12px",
            background: "var(--iris-bg-2)",
            borderTop: "1px solid var(--iris-border)",
          }}
        >
          <span className="iris-meta">{selected.size} selected</span>
          <button
            type="button"
            className="iris-btn"
            disabled={setCoverage.isPending}
            onClick={() => {
              if (
                !window.confirm(
                  `Disable blacklist coverage (DEV only) for ${selected.size} avatar(s)? PROD still excludes them.`,
                )
              )
                return;
              setCoverage.mutate(
                { ids: [...selected], coverageDisabled: true },
                { onSuccess: () => setSelected(new Set()) },
              );
            }}
          >
            Disable coverage · DEV
          </button>
          <button
            type="button"
            className="iris-btn"
            disabled={setCoverage.isPending}
            onClick={() => {
              if (!window.confirm(`Re-enable blacklist coverage for ${selected.size} avatar(s)?`))
                return;
              setCoverage.mutate(
                { ids: [...selected], coverageDisabled: false },
                { onSuccess: () => setSelected(new Set()) },
              );
            }}
          >
            Enable coverage
          </button>
          <button type="button" className="iris-btn" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {addOpen && <AddEntryDialog open={addOpen} onClose={() => setAddOpen(false)} />}
      {bulkOpen && <BulkImportDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />}
    </div>
  );
}
